import React, { useEffect, useState } from 'react';
import { useStore } from './store/store';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { QueryClientProvider } from 'react-query';
import { ConfigProvider } from 'antd';
import { MASKS_TYPE, getPrimaryDark, applyFontsOnThePage } from './constants/constants';
import { formatResContent } from './utils/dataSync';
import ContainerWrapper from './components/Container/ContainerWrapper';
import Container from './components/Container/Container';
import { pageHeight, pageWidth } from './constants/gridConfig';
import { queryClient } from './queryClient';
import { PAGE_BACKGROUND_Z_INDEX, pageBackgrounds } from './utils/pageBackground';

// Observe le textContent d'un élément (créé par le service PDF) et n'appelle
// `onText` que lorsque la valeur change réellement. Remplace le polling
// setInterval : pas de latence, pas de re-render à vide.
const observeTextContent = (elementId, onText) => {
  let last = null;
  const fire = () => {
    const el = document.getElementById(elementId);
    if (!el || !el.textContent) return;
    if (el.textContent === last) return;
    last = el.textContent;
    onText(el.textContent);
  };
  const observer = new MutationObserver(fire);
  // subtree + characterData : capte la création de l'élément comme les mises à
  // jour de son contenu, où qu'il soit dans le DOM.
  observer.observe(document.body, { childList: true, subtree: true, characterData: true });
  fire();
  return () => observer.disconnect();
};

const selector = (state) => ({
  setStore: state.setStore,
  containers: state.containers,
  defaultVariablesApi: state.defaultVariablesApi,
  masks: state.masks,
  landscape: state.landscape,
  fromPdf: state.fromPdf,
  fontFamilyList: state.configuration.fontFamilyList,
  currentDocument: state.currentDocument,
});

const PdfApp = () => {
  const { setStore, containers, defaultVariablesApi, masks, landscape, fromPdf, fontFamilyList, currentDocument } =
    useStore(selector);
  const [containersIdxToShow, setContainersIdxToShow] = useState([])
  const [hiddenContainerIdx, setHiddenContainerIdx] = useState()
  // Container plus haut qu'une page utile : le service demande à n'en montrer
  // qu'une bande horizontale par page (cf. computeSliceBoundaries), le temps de
  // le parcourir en entier.
  const [slice, setSlice] = useState(null)

  useEffect(() => {
    if (fontFamilyList.length > 0) {
      applyFontsOnThePage(fontFamilyList)
    }
  }, [fontFamilyList]);

  useEffect(() => {
    if (fromPdf) {
      return;
    }

    return observeTextContent('editor-store-data', (textContent) => {
      const value = JSON.parse(textContent);
      const rest = value.isTemplate ? {
        currentDocument: { type: 'template' },
        user: { isAdmin: true }
      } : {}
      setStore({
        ...formatResContent(
          value,
          defaultVariablesApi,
          false,
          false,
          !value.isTemplate
        ),
        ...rest
      });
    });
  }, [fromPdf, setStore, defaultVariablesApi]);

  useEffect(() => {
    if (!fromPdf) {
      return;
    }

    return observeTextContent('containers-idx-to-show', (textContent) => {
      const value = JSON.parse(textContent)
      setContainersIdxToShow(value.containersIdxToShow);
      setHiddenContainerIdx(value.containerIdxToHide)
      setSlice(value.slice ?? null)
    });
  }, [fromPdf]);

  const renderHeader = () => {
    const header = masks.find((mask) => mask?.id === MASKS_TYPE.HEADER.id);
    return (
      <div id='header-mask-pdf' style={{ display: 'none' }}>
        {header && (
          <Container id={header.id} key={header.id} />
        )}
      </div>
    );
  };

  const renderFooter = () => {
    const footer = masks.find((mask) => mask?.id === MASKS_TYPE.FOOTER.id);
    return (
      <div id='footer-mask-pdf' style={{ display: 'none' }}>
        {footer && (
          <Container id={footer.id} key={footer.id} />
        )}
      </div>
    );
  };

  // Décor de page : il couvre toute la page, en-tête et pied compris. Chrome ne
  // laisse pas le corps de la page dessiner dans les marges @page où tombent
  // l'en-tête et le pied : le service imprime donc chaque décor seul, en une
  // page sans marge, et la pose sous les pages du document à la fusion (cf.
  // pageBackground côté service). Sans ancêtre positionné, il se place par
  // rapport à la page et non au conteneur (dont l'overflow hidden le couperait).
  //
  // Un document en porte plusieurs : celui du modèle, plus un par section à
  // décor propre. On les rend tous, éteints ; le service allume celui qu'il
  // imprime.
  // Éteints par l'opacité et non par `display: none` : un calque jamais affiché
  // ne chargerait pas ses images, et waitForImagesAndFonts ne les attendrait pas
  // — la première page à l'allumer serait capturée sans elles.
  const renderPageBackgrounds = () =>
    pageBackgrounds(masks).map((background) => (
      <div
        key={background.id}
        className='page-background-layer'
        data-page-background-id={background.id}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          opacity: 0,
          zIndex: PAGE_BACKGROUND_Z_INDEX,
        }}
      >
        <Container id={background.id} key={background.id} />
      </div>
    ));

  if (!fromPdf) return null;

  return (
    <div
      style={{
        width: '100%',
        overflow: 'hidden',
      }}
    >
      {/* Des box appellent les hooks react-query (l'organigramme) même quand
          elles se rendent pour le PDF : sans client, elles plantent au rendu et
          le service attend en vain le container. Aucune requête ne part d'ici
          (pas de proposition dans l'URL de génération). */}
      <QueryClientProvider client={queryClient}>
        <DndProvider backend={HTML5Backend}>
          <ConfigProvider
            theme={{
              token: {
                colorPrimary: getPrimaryDark(currentDocument?.type === 'template'),
              },
            }}
          >
            {renderHeader()}
            {renderFooter()}
            <div id='pdf-app'>
              {typeof hiddenContainerIdx === "number" && <div id={`container-pdf-hidden-${containers[hiddenContainerIdx].id}`} />}
              {containersIdxToShow.map((idx) => {
                const { id, height, spacingAfter } = containers[idx];
                const containerSlice = slice?.containerId === id ? slice : null;

                return <React.Fragment key={id}>
                  <div
                    id={`container-wrapper-${id}`}
                    // Clé de la bande courante : le service l'attend avant de
                    // mesurer. Sans elle, passer d'une bande à la suivante ne
                    // change aucun marqueur du DOM et la mesure porterait sur la
                    // mise en page précédente.
                    data-slice-key={containerSlice ? `${containerSlice.top}-${containerSlice.height}` : undefined}
                    // Espacement à poser sous ce container, lu par la mesure du
                    // service PDF. Porté par les lignes principales promues en
                    // containers (cf. flattenMainRows) pour retrouver dans le PDF
                    // le gap de colonne affiché ici. Absent = interligne du
                    // document.
                    data-spacing-after={spacingAfter}
                    // style={{ backgroundColor: `#${Math.floor(Math.random() * 16777215).toString(16)}` }}
                    className={`pdf-page ${height === (landscape ? pageWidth : pageHeight)
                      ? 'pdf-page-full-height'
                      : 'pdf-page-header-footer'
                      }`}
                  >
                    {containerSlice
                      ? <div style={{ height: containerSlice.height, overflow: 'hidden' }}>
                        <div style={{ marginTop: -containerSlice.top }}>
                          <ContainerWrapper containerId={id} windowItemsIndex={idx + 1} type='container' />
                        </div>
                      </div>
                      : <ContainerWrapper containerId={id} windowItemsIndex={idx + 1} type='container' />}
                  </div>
                  <div id={`container-pdf-${id}`} />
                </React.Fragment>
              })}
            </div>
            {renderPageBackgrounds()}
          </ConfigProvider>
        </DndProvider>
      </QueryClientProvider>
    </div>
  );
};

export default PdfApp
